import React from "react"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import CostCategory  from "./cost_category"
import CategoryBadge from "./category_badge"
import { defaultCostCategoryFor } from "./category_styles"

const canSubmit = (qline) => {
  if (!qline.active) return false

  switch(qline.type) {
    case "break":       return true
    case "subtotal":    return !!qline.quantity
    case "section":     return !!(qline.description && qline.description.trim())
    case "blueprint":   return false
    case "group":       return false
    case "template":    return false
    case "descriptive": return true
    case "product":
      return (
        (qline.quantity && qline.quantity > 0 && qline.quantity >= (qline.quantity_invoiced || 0)) &&
        (qline.unit_price && qline.unit_price != 0) &&
        qline.product.id && qline.vat_rate.id
      )
    case "regular":
      return (
        (qline.quantity && qline.quantity > 0) &&
        (qline.unit_price && qline.unit_price != 0) &&
        qline.vat_rate.id
      )
    case "service":
      if (qline._service_init_mode) return false
      return (
        (qline.quantity && qline.quantity > 0) &&
        qline.vat_rate.id &&
        (qline.qline_breakdowns || []).filter(breakdown => !breakdown._destroy).every(breakdown =>
          parseFloat(breakdown.quantity) > 0 && parseFloat(breakdown.unit_price) > 0
        )
      )
    case "bpu":
      return (
        qline.mbc_line?.id &&
        (qline.quantity && qline.quantity > 0) &&
        (qline.unit_price && qline.unit_price != 0) &&
        qline.vat_rate.id
      )
  }
}

// Propagation is stopped because it will activate or deactivate the lines
export default class Buttons extends React.Component {
  constructor(props) {
    super(props)
    this.state         = {order: props.index + 1, ordering: false}
    this.deactivate    = this.deactivate.bind(this)
    this.destroy       = this.destroy.bind(this)
    this.add           = this.add.bind(this)
    this.onChange      = this.onChange.bind(this)
    this.activateOrder = this.activateOrder.bind(this)
    this.order         = this.order.bind(this)
    this.applyOrder    = this.applyOrder.bind(this)
    this.orderRef      = React.createRef()
  }

  add(e) {
    e.stopPropagation()
    const { index }   = this.props
    const { addLine } = this.props.buttonProps

    addLine("regular", index + 1)
  }

  destroy(e) {
    e.stopPropagation()
    const { index,  qline } = this.props
    const { replaceLine }   = this.props.buttonProps
    if(!qline.can_be_deleted) return

    replaceLine({...qline, destroyed: true}, index)
  }

  deactivate() {
    const { index, qline }  = this.props
    const { replaceLine }   = this.props.buttonProps
    replaceLine({...qline, active: false}, index)
  }

  onChange(e) {
    e.preventDefault()
    const { index, qline } = this.props
    const { replaceLine }  = this.props.buttonProps
    const type = e.target.value
    const withoutValue = {
      unit:           undefined,
      quantity:       undefined,
      unit_price:     undefined,
      purchase_price: undefined
    }
    const costCategory = defaultCostCategoryFor(type)
    const patch        = { type, cost_category: costCategory }
    if (type == "product") {
      replaceLine({...qline, ...patch}, index)
    } else if (type == "break") {
      replaceLine({...qline, ...patch, ...withoutValue, active: false}, index)
    } else if (type == "blueprint") {
      replaceLine({...qline, ...patch}, index)
    } else if (type == "subtotal") {
      replaceLine({...qline, ...patch, ...withoutValue, active: false, description: I18n.t("quotes.bigcard.subtotal"), quantity: 1}, index)
    } else if (type == "section") {
      replaceLine({...qline, ...patch, ...withoutValue, active: false}, index)
    } else if (type == "group") {
      replaceLine({...qline, ...patch}, index)
    } else if (type == "template") {
      replaceLine({...qline, ...patch}, index)
    } else if (type == "descriptive") {
      replaceLine({...qline, ...patch, ...withoutValue}, index)
    } else if (type == "regular") {
      replaceLine({...qline, ...patch}, index)
    } else if (type == "service") {
      const existingBreakdowns = (qline.qline_breakdowns || []).filter(breakdown => !breakdown._destroy)
      if (existingBreakdowns.length) {
        replaceLine({...qline, ...patch, quantity: qline.quantity || 1}, index)
      } else {
        replaceLine({
          ...qline,
          ...patch,
          quantity:           qline.quantity || 1,
          _service_init_mode: "choose",
          qline_breakdowns:   []
        }, index)
      }
    } else if (type == "bpu") {
      replaceLine({...qline, ...patch, quantity: qline.quantity || 1, qline_breakdowns: [], mbc_line: null}, index)
    } else if (type == "import_csv") {
      replaceLine({...qline, ...patch}, index)
    }
  }

  activateOrder(e) {
    e.stopPropagation()
    this.setState({ordering: true})
  }

  order(e) {
    e.preventDefault()
    const value = parseInt(e.target.value)
    if (value && (value < 0)) return

    this.setState({order: e.target.value})
  }

  applyOrder(e) {
    e.stopPropagation()
    if (!this.state.order) return

    const newIndex = parseInt(this.state.order)
    if (!newIndex) return

    const { moveLine } = this.props.buttonProps
    this.setState({ordering: false, order: newIndex})
    moveLine(this.props.index, newIndex - 1)
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.ordering == this.state.ordering) return
    if (!this.orderRef.current) return

    this.orderRef.current.focus()
  }

  render() {
    const { index, qline } = this.props
    const { allowTemplate, allowCsvLine, allowProducts, allowQlineServices, allowQlineGroups, allowQlineBlueprints, hasMbcContract, showCostCategory, enabledCostCategories, replaceLine, overheadCoefs, overheadCoef, saleCoefOverrides } = this.props.buttonProps

    const deleteButtonStyle = qline.can_be_deleted ? {} : { cursor: 'not-allowed' };
    const deleteButtonTooltip = qline.can_be_deleted
        ? I18n.t("quotes.new.remove_line")
        : I18n.t("quotes.new.remove_line_not_allowed");

    return(
      <React.Fragment>
        { qline.active
          ? <div className="text-input" style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label>
                  {I18n.t('quotes.new.qline_type')}
                </label>

                <select
                  className="form-control h-41px"
                  value={qline.type}
                  onChange={this.onChange}
                >
                  { hasMbcContract &&
                    <option value="bpu">
                      {I18n.t('mbc_contracts.mbc_lines.short_label')}
                    </option>
                  }

                  <option value="regular">
                    {I18n.t('quotes.new.line')}
                  </option>

                  { allowQlineServices &&
                    <option value="service">
                      {I18n.t('quotes.new.service')}
                    </option>
                  }

                  { allowQlineGroups &&
                    <option value="group">
                      {I18n.t('quotes.new.qline_group')}
                    </option>
                  }

                  { allowTemplate &&
                    <option value="template">
                      {I18n.t('quotes.new.template')}
                    </option>
                  }

                  { allowQlineBlueprints &&
                    <option value="blueprint">
                      {I18n.t('quotes.new.qline_blueprint')}
                    </option>
                  }

                  <option value="descriptive">
                    {I18n.t('quotes.new.descriptive_line')}
                  </option>

                  { allowProducts &&
                    <option value="product">
                      {I18n.t('quotes.new.product')}
                    </option>
                  }

                  <option value="break">
                    {I18n.t('quotes.new.page_break')}
                  </option>

                  <option value="subtotal">
                    {I18n.t('quotes.new.subtotal')}
                  </option>

                  <option value="section">
                    {I18n.t('quotes.new.section')}
                  </option>

                  { allowCsvLine &&
                    <option value="import_from_csv">
                      {I18n.t('quotes.new.import_from_csv')}
                    </option>
                  }
                </select>
              </div>

              { showCostCategory && qline.type !== "descriptive" && qline.type !== "service" && qline.type !== "bpu" &&
                <div style={{ flex: 1 }}>
                  <CostCategory
                    index              = {index}
                    qline              = {qline}
                    enabledCategories  = {enabledCostCategories}
                    replaceLine        = {replaceLine}
                    overheadCoefs      = {overheadCoefs}
                    overheadCoef       = {overheadCoef}
                    saleCoefOverrides  = {saleCoefOverrides}
                  />
                </div>
              }
            </div>
          : ""
        }

        {
          canSubmit(qline) || this.props.submit
          ? <div className="confirm-button"
                 tooltip={I18n.t("quotes.new.close_editor")}
                 tooltip_position="right"
            >
              <FontAwesomeIcon
                icon="check-circle"
                onClick={this.props.submit || this.deactivate}
              />
            </div>
          : ""
        }

        <div className="quotes--form-line-buttons left">
          <div tooltip={deleteButtonTooltip} tooltip_position="left">
            <FontAwesomeIcon
              className="default"
              style={deleteButtonStyle}
              icon="minus-circle"
              onClick={this.destroy}
            />
          </div>

          <div tooltip={I18n.t("quotes.new.add_line_below")} tooltip_position="left">
            <FontAwesomeIcon
              className="default"
              icon="plus-circle"
              onClick={this.add}
            />
          </div>
        </div>

        { this.state.ordering
          ? <div
              className="quotes--form-line-buttons ordering"
              onClick={e => e.stopPropagation()}
            >
              <input
                type="number"
                step="1"
                className="form-control"
                value={this.state.order}
                onChange={this.order}
                ref={this.orderRef}
              />

              <FontAwesomeIcon icon="check-circle" onClick={this.applyOrder} />
            </div>
          : <div className="quotes--form-line-buttons right d-flex a-i-center" style={{ gap: '12px' }}>
              <span
                className="pointer"
                onClick={this.activateOrder}
                tooltip={I18n.t("quotes.new.modify_order")}
                tooltip_position="right"
              >
                {index + 1}
              </span>
              { showCostCategory &&
                <span
                  className="qline-drag-handle"
                  style={{ cursor: 'grab', display: 'inline-flex' }}
                  onClick={e => e.stopPropagation()}
                >
                  { qline.type === "service" || qline.type === "bpu"
                    ? <CategoryBadge breakdowns={qline.qline_breakdowns || []} />
                    : <CategoryBadge category={qline.cost_category} />
                  }
                </span>
              }
            </div>
        }
      </React.Fragment>
    )
  }
}
